import { useCallback, useContext, useRef } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { Button } from "~/components/Button";
import { QuestionFrame } from "~/components/QuestionFrame";
import { RadioButtonArray } from "~/components/RadioButtonArray";
import { MaskedInput } from "~/components/TextInput";
import type { IMaskedInput } from "~/components/TextInput/MaskedInput";
import { useHemodynamicMeasurements } from "~/modules/hemodynamicMeasurements/useHemodynamicMeasurements";
import { demoStudentEvaluationsRoute } from "~/routes/routes";

import { studentEvaluationDemoContext } from "../../../..";
import { formsLayoutContext } from "../../../../layout";

export const HemodynamicMeasuresForm: React.FC = () => {
	const { data } = useContext(studentEvaluationDemoContext);
	const navigate = useNavigate();
	const { scrollToTop } = useContext(formsLayoutContext);
	const { calcFreqCardiacaMaximaSugerida } = useHemodynamicMeasurements(data);

	const maxHeartFrequencyInputRef = useRef<IMaskedInput>(null);

	const handleSuggestMaxHeartFrequency = useCallback(() => {
		if (!maxHeartFrequencyInputRef.current) return;
		maxHeartFrequencyInputRef.current.setValue(
			calcFreqCardiacaMaximaSugerida(),
		);
	}, [calcFreqCardiacaMaximaSugerida]);

	const handleSave = useCallback(() => {
		scrollToTop();
		navigate(
			`/${demoStudentEvaluationsRoute}/capacidade_aerobica`,

			{ replace: true },
		);
	}, [navigate, scrollToTop]);

	const handleGoBack = useCallback(() => {
		navigate(`/${demoStudentEvaluationsRoute}/avaliacao_corporal`, {
			replace: true,
		});
		scrollToTop();
	}, [navigate, scrollToTop]);

	return (
		<div className="flex flex-col px-6 py-9 gap-6 rounded-2xl bg-white flex-wrap">
			<QuestionFrame title="Medidas" isValid={false}>
				<div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(250px,1fr))] md:block">
					<div className="group flex flex-wrap items-center gap-3">
						<MaskedInput
							ref={maxHeartFrequencyInputRef}
							label="Frequência Cardíaca Máxima (bpm)"
							mask="integer"
							inputMode="numeric"
							placeholder="0"
							value={data?.maxHeartFrequency || 0}
							onChange={() => undefined}
						/>
						<div>
							<Button onClick={handleSuggestMaxHeartFrequency} className="mt-4">
								Sugerir
							</Button>
						</div>
					</div>
					<MaskedInput
						mask="integer"
						inputMode="numeric"
						label="Pressão Sistólica (mmHg)"
						placeholder="Ex.: 120"
						value={data?.systolicArterialPressure || 0}
						onChange={() => undefined}
					/>
					<MaskedInput
						mask="integer"
						inputMode="numeric"
						label="Pressão Diastólica (mmHg)"
						placeholder="Ex.: 80"
						value={data?.diastolicArterialPressure || 0}
						onChange={() => undefined}
					/>
					<MaskedInput
						mask="integer"
						inputMode="numeric"
						label="Frequência Cardíaca em Repouso (bpm)"
						placeholder="0"
						value={data?.restingHeartFrequency || 0}
						onChange={() => undefined}
					/>

					<div className="flex flex-col flex-1 gap-3 min-w-[80px]">
						<span>Haverá prescrição na água?</span>
						<RadioButtonArray
							value={data?.hasInWaterPrescription}
							onChange={() => undefined}
							options={[
								{ label: "Não", value: false },
								{ label: "Sim", value: true },
							]}
						/>
						<span>&nbsp;</span>
					</div>
					{data?.hasInWaterPrescription ? (
						<MaskedInput
							mask="float"
							inputMode="numeric"
							label="Frequência Cardíaca em Repouso na Água (bpm)"
							placeholder="0"
							value={data?.restingHeartFrequencyInWater || 0}
							onChange={() => undefined}
						/>
					) : null}
				</div>
			</QuestionFrame>
			<div className="flex justify-between">
				<Button type="button" onClick={handleGoBack}>
					<FiChevronLeft size={24} />
					<span className="mb-1">Voltar</span>
				</Button>
				<Button loadingMessage="Salvando..." onClick={handleSave}>
					<span className="mb-1">Salvar</span>
					<FiChevronRight size={24} />
				</Button>
			</div>
		</div>
	);
};
